/* ══════════════════════════════════════════════════════════════════
   studyHub — clock dashboard, wandering thoughts, calendar, tasks
   No runtime dependencies. State lives in localStorage.
   ══════════════════════════════════════════════════════════════════ */
import StudyHubEngine from './core/engine.js';
import { DAY_MS, clamp, pad, startOfDay, addDays, startOfWeek, isoDay, fromIsoDay, fmtDay, minToHhmm, hhmmToMin, fmtDurMin, toLocalInput } from './core/time.js';
import { ltKind, ltBand, wkKind, wkMode, seriesKind, quantityAttr, choiceAttrs, taskShape, median, estBaseline, wasStarted, elapsedSec } from './core/domain.js';
import { standingMultiple, historyStats, rateStats, deadlineById, tasksForDeadline } from './state/evidence.js';
import { PLAN, WEEK, touchPlan, recomputePlans } from './state/plans.js';
import { S, load, save, onSaveFailure, courseById, courseName } from './state/store.js';
import { SYNC } from './platform/bridge.js';
import { fmtMinOfDay } from './ui/format.js';
import { $, toast, closeModal, setDrawer, openModals } from './ui/dom.js';
import { initSettings, onLookChanged } from './ui/appearance.js';
import { KEYS, storage } from './state/storage.js';
import { leavingForRestore } from './ui/appearance.js';
import { initThoughtsTab, renderBoardView, updateThoughtChrome } from './ui/board.js';
import { initCalendarTab, renderCalendar, renderScheduled } from './ui/calendar.js';
import { applyBackground, initClockTab, renderClock, renderGallery } from './ui/clock.js';
import { initLongtermTab, initPasteAnywhere, renderLongterm } from './ui/longterm.js';
import { generateSeries, initSeries, instanceOf, occurrences, renderSeriesPanel } from './ui/series.js';
import { closeSession, confirmFinish, initPanel, renderCourseSelects, renderList, renderStanding, renderStats, sessionId, setFormType, updateEstPreview, updateFinishMult } from './ui/tasks.js';
import { initWeeklyTab, renderWeekly } from './ui/weekly.js';
import { initTabs, showTab } from './ui/shell.js';
import { confirmPasteText } from './ui/longterm.js';
/* ───────────────────────── small helpers ───────────────────────── */


function closeTopModal() { if (openModals.length) closeModal(openModals[openModals.length - 1]); }

document.addEventListener('click', (e) => {
  const c = /** @type {any} */ (/** @type {Element} */ (e.target).closest('[data-close]'));
  if (c) closeModal(c.dataset.close);
});
$('overlay').addEventListener('click', closeTopModal);

/* Escape unwinds one layer at a time: the top modal first, and only once none
   is left does it leave the session view. */
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  if (openModals.length) closeTopModal();
  else if (document.body.classList.contains('settings-open')) setDrawer(null);
  else if (sessionId) closeSession();
});






/* ═════════════════════════ 1. CLOCK + BACKGROUND ═════════════════════════ */







/* ═════════════════════════ 2. WANDERING THOUGHTS ═════════════════════════ */












































/* ═════════════════════════ 3. TASKS, TIMERS, MULTIPLES ═════════════════════════ */






/* ── panel form ── */
































































/* ═════════════════════════ 4. GOOGLE CALENDAR ═════════════════════════ */














/* ─────────────────── calendar view state ─────────────────── */








/* ─────────────────── week view ─────────────────── */



/* ─────────────────── month view ─────────────────── */


/* ─────────────────── agenda view ─────────────────── */


/* ─────────────────── calendar dispatch ─────────────────── */



/* ─────────────────── init ─────────────────── */







/* ─────────────────── the plan, worked out here ─────────────────── */









/* ─────────────────── the horizon, drawn ─────────────────── */







/* ─────────────────── the syllabus drop ─────────────────── */














/* ─────────────────── adding and editing an entry ─────────────────── */








/* ─────────────────── what the weighing found ─────────────────── */







/* ─────────────────── the tab as a whole ─────────────────── */








/** Monday-first index for a Date, matching how startOfWeek lays the week out. */



/* ─────────────────── the plan, worked out here ─────────────────── */






/* ─────────────────── drawing the week ─────────────────── */




/* ─────────────────── missing a block, and moving it ─────────────────── */




/* ─────────────────── the items list, and editing one ─────────────────── */










/* ─────────────────── how the week was weighed ─────────────────── */



/* ─────────────────── what the layout says about itself ─────────────────── */


/* ─────────────────── the tab as a whole ─────────────────── */








/* ─────────────────── generating the instances ─────────────────── */



/* ─────────────────── the intake, before the clock ─────────────────── */













/* ── the template editor ── */





/* ── what has been measured, shown back ── */











/* ═════════════════════════ 9. TABS + BOOT ═════════════════════════ */


function boot() {
  /* The store cannot show a message and should not know how. A failed write is
     the one moment the user must hear about — it is the moment work stops being
     saved — so the handler is installed before anything is read. */
  onSaveFailure((message) => toast(message, true));

  /* The look is owned elsewhere; the board is owned here. Notes carry inline
     colours picked from the template's palette rather than classes, so a
     template change has to repaint them. */
  onLookChanged(() => { renderBoardView(); updateThoughtChrome(); });

  /* First, before the state is even read. The engine takes its dependencies by
     hand rather than reaching for them off the global scope: every one of them
     is then visible in this one list, the load order stops mattering, and the
     engine can be driven from node against a synthetic state with no DOM.

     It throws on a missing binding, which is why it goes here — a planner that
     silently produced an empty term because one helper was renamed would be
     the worst failure this app could have, and boot is where that has to
     surface. The state accessors are functions so the engine always reads the
     live object rather than whatever `S` happened to be at boot. */
  StudyHubEngine.install({
    state: () => S, sync: () => SYNC, now: () => Date.now(),
    DAY_MS, startOfDay, addDays, startOfWeek, isoDay, fromIsoDay,
    hhmmToMin, minToHhmm, pad, clamp, fmtDay, fmtDurMin, fmtMinOfDay,
    courseName, courseById, wkKind, wkMode, seriesKind, quantityAttr, choiceAttrs,
    ltKind, ltBand, taskShape, median, elapsedSec, estBaseline, wasStarted,
    deadlineById, tasksForDeadline, occurrences, instanceOf,
    standingMultiple, historyStats, rateStats,
  });

  load();

  initSettings();    // before the render pass, so nothing paints twice
  initTabs();
  initClockTab();
  initThoughtsTab();
  initPanel();
  initCalendarTab();
  initLongtermTab();
  $('pasteSave').addEventListener('click', confirmPasteText);
  initPasteAnywhere();
  initWeeklyTab();
  initSeries();

  /* Before the first render, so a week's worth of recurring work is already in
     the task list rather than appearing a moment later. The tabs above have
     been wired but nothing has drawn yet, so nothing has been counted yet
     either; this says so explicitly rather than relying on that ordering
     holding for ever. */
  generateSeries();
  touchPlan();

  $('finishConfirm').addEventListener('click', confirmFinish);
  ['finishH', 'finishM'].forEach((id) => $(id).addEventListener('input', updateFinishMult));

  // prefill event times with the next round hour
  const soon = new Date(Date.now() + 3600e3); soon.setMinutes(0, 0, 0);
  $('fEvStart').value = toLocalInput(soon);
  $('fEvEnd').value = toLocalInput(new Date(soon.getTime() + 3600e3));

  setFormType('task');
  renderCourseSelects();
  [...$('listSeg').children].forEach((x) => x.classList.toggle('is-active', x.dataset.list === S.ui.list));

  applyBackground();
  renderGallery();
  renderBoardView();
  renderList();
  renderStats();
  renderStanding();
  renderScheduled();
  renderSeriesPanel();
  updateEstPreview();
  renderClock();
  showTab(S.ui.tab || 'clock');

  setInterval(renderClock, 500);

  /* A running timer must survive a reload, so whatever is in memory is written
     on the way out.

     Except when the reload *is* the point. Restoring a backup writes the new
     state to storage and then reloads to pick it up — and this handler would
     fire in between and put the old, in-memory state straight back over it,
     so the restore would appear to do nothing at all. `leavingForRestore` is
     how the one caller that has already written storage says so. */
  window.addEventListener('beforeunload', () => {
    if (leavingForRestore) return;
    try { storage().write(KEYS.main, JSON.stringify(S)); } catch (e) {}
  });

  if (!S.tasks.length && !S.events.length && !S.thoughts.length) {
    setDrawer('form');
  }
}

/* ─────────────────── the handle ───────────────────

   app.js is a module now, so none of the above is on `window` any more — which
   is the point of the exercise. But two callers still legitimately need a way
   in: the browser console, when something looks wrong on a machine that is not
   this one, and the end-to-end suite, which drives the app the way a person
   does and cannot reach module scope.

   So: one named object listing exactly what may be reached from outside,
   instead of the old arrangement where everything was reachable because
   everything was global. Anything not named here is internal, and the compiler
   will now say so when it stops being used. */

window.studyHub = {
  showTab,
  save,
  state: () => S,
  plans: () => ({ PLAN, WEEK }),
  recompute: (force = true) => { recomputePlans(force); return { PLAN, WEEK }; },
  render: {
    list: renderList,
    calendar: renderCalendar,
    longterm: renderLongterm,
    weekly: renderWeekly,
  },
  engine: StudyHubEngine,
};

boot();
